/**
 * Onboarding questions service, backed by Firestore.
 * Responses live at users/{uid}/onboarding/{questionKey}; submitting also
 * flips onboardingCompleted on the profile doc (which ProtectedRoute gates on).
 */

import { collection, doc, getDoc, getDocs, updateDoc, writeBatch } from 'firebase/firestore';
import { auth, db } from '../../../../shared/firebase';

function requireUid() {
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error('Not signed in');
  return fbUser.uid;
}

/**
 * Submit all onboarding responses.
 * @param {Array<{questionKey: string, answerValue: string}>} responses
 * @returns {Promise<{success: boolean, user: object}>}
 */
export async function submitOnboardingResponses(responses) {
  const uid = requireUid();
  const answeredAt = new Date().toISOString();

  const batch = writeBatch(db);
  (responses || []).forEach(({ questionKey, answerValue }) => {
    batch.set(doc(db, 'users', uid, 'onboarding', questionKey), {
      questionKey,
      answerValue,
      answeredAt,
    });
  });
  await batch.commit();

  await updateDoc(doc(db, 'users', uid), { onboardingCompleted: true });

  const userSnap = await getDoc(doc(db, 'users', uid));
  return { success: true, user: userSnap.exists() ? userSnap.data() : null };
}

/**
 * Get existing onboarding responses for the current user.
 * @returns {Promise<Array<{questionKey, answerValue, answeredAt}>>}
 */
export async function getExistingResponses() {
  const uid = requireUid();
  const snap = await getDocs(collection(db, 'users', uid, 'onboarding'));
  const responses = [];
  snap.forEach((d) => responses.push(d.data()));
  return responses;
}
